// ==============================
// 파일명 : src/lib/firestore.js
// 역할 : Firestore CRUD와 언어팩/영상/유저 데이터
//         동기화 및 UID 기반 사용자 저장 유틸을 제공
// 수정일 : 2025-10-13
// ==============================

import { db } from "./firebase";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import {
  FIRESTORE_COLLECTIONS,
  FIRESTORE_FIELDS,
  getChaptersPath,
} from "../constants/firestore"; // [수정] Firestore 상수 사용
import { USER_FIELD_AUTH_UID } from "../constants/users/fields"; // [수정] 사용자 UID 필드 상수
import {
  normalizePackIds,
  arePackIdListsEqual,
  markOwnedPackActivityTime,
  normalizeAuthUid,
} from "../utils/users"; // [수정] 보유팩 정규화 및 활동 시각 유틸

// 공용
const col = (name) => collection(db, name); // [수정] 대상 컬렉션 참조 생성
const byId = (name, id) => doc(db, name, id); // [수정] 문서 경로 참조 생성
export const uid = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`; // [수정] 고유 ID 생성

const ensureAuthUidField = async (docRef, authUid) => {
  if (authUid) return; // [수정] 이미 UID가 있으면 추가 작업 불필요
  await setDoc(docRef, { [USER_FIELD_AUTH_UID]: docRef.id }, { merge: true }); // [수정] 문서 ID를 UID 필드에 기록
}; // [수정] UID 필드 비어 있을 때 문서 ID로 채워 넣는다.

// Firestore 호출이 너무 오래 걸릴 때를 대비한 안전장치
const FIRESTORE_TIMEOUT_MS = 8000; // [수정] 요청 제한 시간(ms)
const withTimeout = (
  promise,
  ms = FIRESTORE_TIMEOUT_MS,
  message = "Firestore request timed out"
) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(message)), ms)
    ),
  ]); // [수정] 시간 초과 보호 래퍼

// 안전 래퍼
async function safeGetDocs(colRefOrQuery) {
  const snap = await withTimeout(getDocs(colRefOrQuery));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
} // [수정] 문서 배열 안전 조회

const nowMs = () => Date.now(); // [수정] 현재 시각(ms) 반환

const upsertPackVideoUpdate = (packId, timestamp, extras = {}) =>
  setDoc(
    byId(FIRESTORE_COLLECTIONS.PACK_VIDEO_UPDATES, packId),
    {
      [FIRESTORE_FIELDS.PACK_ID]: packId,
      [FIRESTORE_FIELDS.VIDEOS_UPDATED_AT]: timestamp,
      ...extras,
    },
    { merge: true }
  ); // [수정] 언어팩 영상 갱신 요약 문서를 저장한다.

const touchPackVideosUpdatedAt = async (packId, extras = {}) => {
  if (!packId) return; // [수정] packId 없으면 중단
  const timestamp = nowMs(); // [수정] 갱신 시각 계산
  await Promise.all([
    setDoc(
      byId(FIRESTORE_COLLECTIONS.WORD_PACKS, packId),
      { [FIRESTORE_FIELDS.VIDEOS_UPDATED_AT]: timestamp },
      { merge: true }
    ),
    upsertPackVideoUpdate(packId, timestamp, extras),
  ]);
  return timestamp; // [수정] 호출자에게 갱신 시각 반환
}; // [수정] 언어팩 영상 업데이트 시각을 두 컬렉션에 기록한다.

const removePackVideoUpdate = async (packId) => {
  if (!packId) return; // [수정] packId 없으면 중단
  await deleteDoc(byId(FIRESTORE_COLLECTIONS.PACK_VIDEO_UPDATES, packId));
}; // [수정] 언어팩 영상 갱신 요약 문서를 제거한다.

const prepareVideoPayload = (data = {}, packId) => {
  const payload = { ...data };
  if (packId && !payload.packId) payload.packId = packId; // [수정] packId 보강
  payload[FIRESTORE_FIELDS.UPDATED_AT] = nowMs();
  return payload;
}; // [수정] 영상 저장용 공통 페이로드 생성

const resolveVideoPackId = async (packId, videoId) => {
  if (packId || !videoId) return packId || null; // [수정] 전달된 packId 우선 사용
  const snap = await withTimeout(
    getDoc(byId(FIRESTORE_COLLECTIONS.PACKS_YOUTUBE, videoId))
  );
  if (!snap.exists()) return null; // [수정] 문서 없음 처리
  const data = snap.data();
  return data?.packId || null; // [수정] 문서 내 packId 반환
}; // [수정] 영상 문서에서 packId 확인

// Admins
export const getAdmins = async () =>
  safeGetDocs(col(FIRESTORE_COLLECTIONS.ADMINS)); // [수정] 관리자 목록 조회
export const addAdmin = (data) =>
  addDoc(col(FIRESTORE_COLLECTIONS.ADMINS), data); // [수정] 관리자 추가
export const updateAdmin = (id, data) =>
  setDoc(byId(FIRESTORE_COLLECTIONS.ADMINS, id), data, { merge: true }); // [수정] 관리자 갱신
export const deleteAdmin = (id) =>
  deleteDoc(byId(FIRESTORE_COLLECTIONS.ADMINS, id)); // [수정] 관리자 삭제

// Users
export const getUsers = async () =>
  safeGetDocs(col(FIRESTORE_COLLECTIONS.USERS)); // [수정] 사용자 목록 조회
export const getUserById = async (id) => {
  const snap = await withTimeout(
    getDoc(byId(FIRESTORE_COLLECTIONS.USERS, id))
  ); // [2025-09-27 PATCH][수정] 단일 사용자 조회
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};
export const addUser = async (data = {}) => {
  const payload = { ...data }; // [수정] 불변성을 위해 복사본 사용
  const desiredUid = normalizeAuthUid(payload[USER_FIELD_AUTH_UID]); // [수정] 요청된 UID 정규화
  if (desiredUid) {
    payload[USER_FIELD_AUTH_UID] = desiredUid; // [수정] UID 필드 확정
    const docRef = byId(FIRESTORE_COLLECTIONS.USERS, desiredUid); // [수정] 지정 UID 기반 문서 참조
    await setDoc(docRef, payload, { merge: true }); // [수정] UID와 동일한 문서 ID로 저장
    return desiredUid; // [수정] 실제 문서 ID 반환
  }
  const docRef = await addDoc(col(FIRESTORE_COLLECTIONS.USERS), payload); // [수정] UID 미지정 시 자동 ID 생성
  await ensureAuthUidField(docRef, payload[USER_FIELD_AUTH_UID]); // [수정] UID 필드가 없으면 문서 ID로 채움
  return docRef.id; // [수정] 생성된 문서 ID 반환
}; // [수정] 사용자 추가 시 UID와 문서 ID 동기화
export const updateUser = (id, data) =>
  setDoc(byId(FIRESTORE_COLLECTIONS.USERS, id), data, { merge: true }); // [수정] 사용자 갱신
export const deleteUser = (id) =>
  deleteDoc(byId(FIRESTORE_COLLECTIONS.USERS, id)); // [수정] 사용자 삭제

// Word Packs
export const getWordPacks = async () => {
  const list = await safeGetDocs(col(FIRESTORE_COLLECTIONS.WORD_PACKS));
  return list
    .filter((item) => !item.isDeleted) // [수정] 삭제 플래그가 있는 언어팩은 제외한다.
    .map((item) => {
      const wordPackId = item.wordPackId ?? item.id;
      return { ...item, wordPackId, id: item.id };
    });
};
export const addWordPack = async (data) => {
  const wordPackId = uid("wordPack");
  const docRef = byId(FIRESTORE_COLLECTIONS.WORD_PACKS, wordPackId);
  await setDoc(docRef, { ...data, wordPackId });
  return { id: wordPackId, wordPackId };
};
export const updateWordPack = (id, data) =>
  setDoc(byId(FIRESTORE_COLLECTIONS.WORD_PACKS, id), { ...data, wordPackId: id }, { merge: true }); // [수정] 언어팩 정보 갱신
// [수정] 언어팩 삭제 시에는 실제 문서를 지우지 않고 삭제 정보를 기록한다.
export const markWordPackDeleted = (id, extra = {}) =>
  setDoc(
    byId(FIRESTORE_COLLECTIONS.WORD_PACKS, id),
    {
      isDeleted: true,
      deletedAt: Date.now(),
      ...extra,
    },
    { merge: true }
  );

// [수정] 언어팩 삭제 시 연관된 챕터/영상/플래그를 모두 정리한다.
export const getVideosByPack = async (packId) => {
  if (!packId) return []; // [수정] packId 없으면 빈 배열
  return safeGetDocs(
    query(
      col(FIRESTORE_COLLECTIONS.PACKS_YOUTUBE),
      where("packId", "==", packId)
    )
  ); // [수정] 언어팩별 영상 조회
};

export const getVideosByChapterIds = async (chapterIds = []) => {
  const cleanIds = Array.from(
    new Set((Array.isArray(chapterIds) ? chapterIds : []).map((id) => String(id || "").trim()).filter(Boolean))
  );
  if (!cleanIds.length) return [];

  const chunks = [];
  const CHUNK_SIZE = 10;
  for (let i = 0; i < cleanIds.length; i += CHUNK_SIZE) {
    chunks.push(cleanIds.slice(i, i + CHUNK_SIZE));
  }

  const snapshots = await Promise.all(
    chunks.map((chunk) =>
      safeGetDocs(
        query(
          col(FIRESTORE_COLLECTIONS.PACKS_YOUTUBE),
          where("chapterId", "in", chunk)
        )
      )
    )
  );
  return snapshots.flat();
};

export const inspectWordPackContents = async (packId) => {
  const chapters = await getChaptersByPack(packId);
  const chapterIds = chapters.map((chapter) => chapter.chapterId).filter(Boolean);
  const [videosByPack, videosByChapter] = await Promise.all([
    getVideosByPack(packId),
    chapterIds.length ? getVideosByChapterIds(chapterIds) : Promise.resolve([]),
  ]);

  const videoMap = new Map();
  for (const video of [...videosByPack, ...videosByChapter]) {
    if (!video?.id) continue;
    videoMap.set(video.id, video);
  }

  return {
    chapters,
    videos: Array.from(videoMap.values()),
    chapterCount: chapters.length,
    videoCount: videoMap.size,
  };
};

export const deleteWordPackCascade = async (packId) => {
  const { chapters, videos, chapterCount, videoCount } = await inspectWordPackContents(packId);
  const chaptersCol = collection(db, getChaptersPath(packId)); // [수정] 언어팩 챕터 경로 사용
  await Promise.all(
    chapters.map((chapter) => {
      const targetId = chapter.docId || chapter.chapterId || chapter.id;
      if (!targetId) return Promise.resolve();
      return deleteDoc(doc(chaptersCol, targetId));
    })
  );

  const uniqueVideoIds = Array.from(new Set(videos.map((video) => video.id).filter(Boolean)));
  if (uniqueVideoIds.length) {
    await Promise.all(
      uniqueVideoIds.map((videoId) =>
        deleteDoc(byId(FIRESTORE_COLLECTIONS.PACKS_YOUTUBE, videoId))
      )
    ); // [수정] 영상 컬렉션 문자열을 상수로 통일
  }

  await removePackVideoUpdate(packId);
  
  return { chapterCount, videoCount };
};


// Chapters (하위 컬렉션)
export const getChaptersByPack = async (packId) => {
  const chaptersCol = collection(db, getChaptersPath(packId)); // [수정] 언어팩 챕터 경로 사용
  const list = await safeGetDocs(query(chaptersCol));
  const normalized = list.map((item) => {
    const chapterId = item.chapterId ?? item.id;
    const chapter = item.chapter ?? item.chapterKey ?? item.id;
    return { ...item, id: chapterId, chapter, chapterId, docId: item.id };
  });
  return normalized.sort((a, b) => {
    const ao = a.order ?? 9999;
    const bo = b.order ?? 9999;
    if (ao !== bo) return ao - bo;
    return (a.chapter || "").localeCompare(b.chapter || "");
  });
};
export const upsertChapter = async (packId, chapterId, data = {}) => {
  const chaptersCol = collection(db, getChaptersPath(packId)); // [수정] 언어팩 챕터 경로 사용
  const resolvedId = chapterId || uid("chapter");
  await setDoc(doc(chaptersCol, resolvedId), { ...data, chapterId: resolvedId }, { merge: true });
  return resolvedId;
};
export const deleteChapter = async (packId, chapterId) => {
  const chaptersCol = collection(db, getChaptersPath(packId)); // [수정] 언어팩 챕터 경로 사용
  await deleteDoc(doc(chaptersCol, chapterId));
};

// Videos
export const getVideos = async () =>
  safeGetDocs(col(FIRESTORE_COLLECTIONS.PACKS_YOUTUBE)); // [수정] 전체 영상 조회

export const getPackVideoUpdateMap = async () => {
  const list = await safeGetDocs(
    query(
      col(FIRESTORE_COLLECTIONS.PACK_VIDEO_UPDATES),
      orderBy(FIRESTORE_FIELDS.VIDEOS_UPDATED_AT, "desc")
    )
  );
  return list.map((item) => ({
    packId: item[FIRESTORE_FIELDS.PACK_ID] || item.id,
    [FIRESTORE_FIELDS.VIDEOS_UPDATED_AT]:
      item[FIRESTORE_FIELDS.VIDEOS_UPDATED_AT] || 0,
    name: item[FIRESTORE_FIELDS.PACK_NAME] || item.name || "", // [수정] 기존 스키마와 호환되는 이름 필드
  }));
}; // [수정] 언어팩 영상 갱신 요약 조회

export const getLatestPackVideosUpdatedAt = async () => {
  const [latest] = await safeGetDocs(
    query(
      col(FIRESTORE_COLLECTIONS.PACK_VIDEO_UPDATES),
      orderBy(FIRESTORE_FIELDS.VIDEOS_UPDATED_AT, "desc"),
      limit(1)
    )
  );
  return latest?.[FIRESTORE_FIELDS.VIDEOS_UPDATED_AT] || null;
}; // [수정] 가장 최근 영상 갱신 시각 조회

export const addVideo = async (data = {}) => {
  const payload = prepareVideoPayload(data, data.packId);
  const docRef = await addDoc(
    col(FIRESTORE_COLLECTIONS.PACKS_YOUTUBE),
    payload
  );
  await touchPackVideosUpdatedAt(payload.packId);
  return docRef;
}; // [수정] 영상 추가 + 갱신 시각 기록

export const updateVideo = async (...args) => {
  let packId;
  let videoId;
  let data;
  if (args.length === 3) {
    [packId, videoId, data] = args;
  } else {
    [videoId, data] = args;
    packId = data?.packId;
  }
  if (!videoId) throw new Error("videoId is required");
  const resolvedPackId = await resolveVideoPackId(packId, videoId);
  const payload = prepareVideoPayload(data, resolvedPackId || packId);
  await setDoc(
    byId(FIRESTORE_COLLECTIONS.PACKS_YOUTUBE, videoId),
    payload,
    { merge: true }
  );
  await touchPackVideosUpdatedAt(resolvedPackId || packId);
}; // [수정] 영상 갱신 + 갱신 시각 기록

export const deleteVideo = async (packOrVideoId, maybeVideoId) => {
  const hasPackId = typeof maybeVideoId === "string";
  const videoId = hasPackId ? maybeVideoId : packOrVideoId;
  const packId = hasPackId ? packOrVideoId : null;
  if (!videoId) throw new Error("videoId is required for delete");
  const resolvedPackId = await resolveVideoPackId(packId, videoId);
  await deleteDoc(byId(FIRESTORE_COLLECTIONS.PACKS_YOUTUBE, videoId));
  await touchPackVideosUpdatedAt(resolvedPackId);
}; // [수정] 영상 삭제 + 갱신 시각 기록

// Devices (user_devices)
export const getDevicesByUser = async (userId) => {
  const qy = query(
    col(FIRESTORE_COLLECTIONS.DEVICE_REGISTRY),
    where("userId", "==", userId)
  ); // [수정] 특정 사용자 기기 조회
  return safeGetDocs(qy);
};
export const deleteDevice = async (docId) =>
  deleteDoc(byId(FIRESTORE_COLLECTIONS.DEVICE_REGISTRY, docId)); // [수정] 사용자 기기 삭제

// [수정] 언어팩이 다른 컬렉션에서 사용 중인지 확인한다.
export const getUsersByOwnedPack = async (packId) => {
  const ownedQuery = query(
    col(FIRESTORE_COLLECTIONS.USERS),
    where("ownedPacks", "array-contains", packId)
  );
  return safeGetDocs(ownedQuery);
};

export const getPurchasesByPack = async (packId) => {
  const purchaseQuery = query(
    col(FIRESTORE_COLLECTIONS.USER_PURCHASES),
    where("packId", "==", packId)
  );
  return safeGetDocs(purchaseQuery);
};

// [수정] 유저 상태 필드를 갱신하기 위한 헬퍼.
export const updateUserStatus = async (userId, statusPayload = {}) =>
  setDoc(
    byId(FIRESTORE_COLLECTIONS.USERS, userId),
    {
      accountStatus: {
        state: statusPayload.state || "active",
        updatedAt: statusPayload.updatedAt || Date.now(),
        note: statusPayload.note || "",
        scheduledDeletionAt: statusPayload.scheduledDeletionAt || null,
      },
    },
    { merge: true }
  );

// [수정] 탈퇴한 유저는 1년 뒤 자동 삭제를 위해 스케줄을 기록한다.
export const markUserWithdrawn = async (userId) => {
  const now = Date.now();
  const oneYearLater = now + 365 * 24 * 60 * 60 * 1000;
  await updateUserStatus(userId, {
    state: "withdrawn",
    updatedAt: now,
    note: "사용자 요청으로 탈퇴 처리됨",
    scheduledDeletionAt: oneYearLater,
  });
};

// [수정] 삭제 처리 시에는 관련 user_purchases 문서를 즉시 제거한다.
export const deleteUserImmediately = async (userId) => {
  const purchases = await safeGetDocs(query(col("user_purchases"), where("userId", "==", userId)));
  await Promise.all(purchases.map((purchase) => deleteDoc(byId("user_purchases", purchase.id))));
  await deleteDoc(byId("users", userId));
};

// [수정] 모달에서 구매 내역을 보여주기 위해 사용자별 구매 문서를 조회한다.
export const getPurchasesByUser = async (userId) => {
  const purchaseQuery = query(col("user_purchases"), where("userId", "==", userId));
  return safeGetDocs(purchaseQuery);
};

// [수정] 탈퇴 유저 목록을 시간순으로 가져오기 위한 함수.
export const getWithdrawnUsers = async () => {
  const withdrawnQuery = query(col("users"), where("accountStatus.state", "==", "withdrawn"), orderBy("accountStatus.updatedAt", "desc"));
  return safeGetDocs(withdrawnQuery);
};

/* ============================================
   [2025-09-27 PATCH]
   유저 보유 팩 저장(setUserOwnedPacks)
   - 저장 위치 1: user_purchases 컬렉션 (flat)
       docId 규칙 예: `${userId}_${packId}`
   - 저장 위치 2: users/{id}.ownedPacks 필드
   프로젝트 정책에 맞게 한쪽만 써도 되고, 둘 다 써도 됨.
   아래 함수는 둘 다 동기화하도록 구현.
   ============================================ */
export async function setUserOwnedPacks(userId, packIds = []) {
const normalized = normalizePackIds(packIds); // [수정] 입력 배열 정규화
  const hasPacks = normalized.length > 0; // [수정] 보유 여부 판단
  const userRef = byId("users", userId); // [수정] 대상 사용자 문서 참조

  const userSnap = await withTimeout(getDoc(userRef)); // [수정] 기존 데이터 조회
  const prevData = userSnap.exists() ? userSnap.data() : {}; // [수정] 이전 데이터 확보
  const prevOwned = normalizePackIds(prevData?.ownedPacks); // [수정] 이전 보유팩 정규화
  const changed = !arePackIdListsEqual(prevOwned, normalized); // [수정] 변경 여부 계산

  const basePayload = { ownedPacks: hasPacks ? normalized : null }; // [수정] 기본 저장 페이로드
  const payload = changed
    ? {
        ...basePayload,
        ownedPacksUpdate: hasPacks
          ? markOwnedPackActivityTime()
          : null,
      }
    : basePayload; // [수정] 변경 시 타임스탬프 기록, 전부 삭제 시 null 반영

  await setDoc(userRef, payload, { merge: true }); // [수정] 사용자 문서 저장

  // B) user_purchases 동기화 (단순화: 기존 것을 지우고 다시 쓰기)
  //    규모가 커지면 서버 함수로 마이그레이션 권장
  const purchases = await safeGetDocs(query(col("user_purchases"), where("userId", "==", userId)));
  // 삭제
  await Promise.all(purchases.map((p) => deleteDoc(byId("user_purchases", p.id))));
  if (!hasPacks) {
    return;
  }

  // 추가
  const now = Date.now();
  await Promise.all(
    normalized.map((pid) =>
      addDoc(col("user_purchases"), {
        userId,
        packId: pid,
        purchasedAt: now,
        version: 1,
      })
    )
  );
}
